<template>
	<datatable
		:headers
		:value="items"
		:filter-options="filterOptions"
		:global-filter-fields="filterFields"
		:body-row-class-name="(item: CollectionItem) => `${item.complete_flag ? 'actioned ' : ''}`"
		:loading>
		<template #filter-journey_no="{ model, callback }">
			<app-dropdown
				v-if="model && getJourneyNumberOptions(items).length > 0"
				v-model="model.value"
				label-key="value"
				size="small"
				:options="getJourneyNumberOptions(items)"
				@update:model-value="callback()">
				Journey No
			</app-dropdown>
		</template>

		<template #row-desc="{ row }">
			<div v-if="row.type !== 'Component'">
				{{ row.desc }}
			</div>
			<div v-else>
				<span class="ml-3">{{ `&#x2022; ${row.desc}` }}</span>
			</div>
		</template>

		<template #row-status="{ row }">
			<div class="flex items-center mt-2 mb-2">
				<app-badge class="gap-2" size="small" :theme="getStatusClass(row.status)">
					{{ row.status }}
				</app-badge>
			</div>
		</template>

		<template #row-complete_flag="{ row }">
			<app-checkbox
				v-model="row.complete_flag"
				wrapper=""
				name="complete_flag"
				:true-value="true"
				:false-value="false"
				@update:model-value="emit('updatedItems', row)" />
		</template>

		<template #row-notes="{ row }">
			<notes-button
				:status="{ notes: row.notes, notes_status: row.notes_status }"
				@click="editing = row" />
		</template>
	</datatable>

	<edit-collection-item-note
		:item="editing"
		:resolve-allowed="true"
		:resolve-on-save="true"
		@updated="emit('updatedNote')"
		@closed="editing = undefined" />
</template>

<script lang="ts" setup>
	import type { Header } from '@/data/types/datatable'
	import { computed, ref } from 'vue'
	import { getStatusClass } from '@/composables/datatable.js'
	import { CollectionItem } from '@/data/schema/collection-item'
	import EditCollectionItemNote from '@/components/partials/cat2/EditCollectionItemNote.vue'
	import NotesButton from '@/components/partials/cat2/NotesButton.vue'
	import useCollections from '@/composables/collections'
	import { FilterMatchMode } from '@primevue/core/api'

	const editing = ref<CollectionItem | undefined>(undefined)
	const { getJourneyNumberOptions } = useCollections()

	const props = defineProps({
		items: {
			type: Array<CollectionItem>,
			default: []
		},
		loading: Boolean
	})

	const headers = ref<Header[]>([
		{ value: 'status', text: 'Status', props: { width: 100 } },
		{ value: 'item_number', text: 'Item No', props: { sortable: true, width: 100 } },
		{ value: 'item_description', text: 'Item Description', props: { width: 250 } },
		{ value: 'type', text: 'Type', props: { sortable: true } },
		{ value: 'contract_number', text: 'Cont No', props: { sortable: true } },
		{ value: 'journey_number', text: 'Journey No', props: { sortable: true } },
		{ value: 'item_weight', text: 'Weight', props: { sortable: true } },
		{ value: 'quantity_on_hire', text: 'OH Qty', props: { sortable: true } },
		{ value: 'quantity_recorded', text: 'Qty Rec', props: { sortable: true } },
		{ value: 'quantity_ok', text: 'OK', props: { sortable: true } },
		{ value: 'quantity_damaged', text: 'DAM' },
		{ value: 'damage_code', text: 'DAM CODE' },
		{ value: 'notes', text: 'Notes' },
		{ value: 'complete_flag', text: 'Complete' }
	])

	const filterOptions = ref({
		journey_number: { value: null, matchMode: FilterMatchMode.EQUALS }
	})

	const filterFields = computed(() => {
		let fields = []

		if (getJourneyNumberOptions(props.items).length > 1) {
			fields.push('journey_number')
		}

		return fields
	})

	// Events
	const emit = defineEmits(['updatedItems', 'updatedNote'])
</script>
